/**
 * `/cases`: "Mes relances" — the caller's own registration cases, open
 * first, each with its follow-up sequence and the actions that advance it
 * (`docs/API.md` §7). No `BottomNav` here: `AppShell` supplies the shell.
 */
import { useEffect, useState } from 'react';

import { listMyRegistrationCases } from '../../../api/household';
import { Skeleton } from '../../../components/ui';
import { CaseCard } from '../components/CaseCard';
import { t } from '../../../i18n/fr';

import type { RegistrationCase } from '../../../api/types';

/**
 * Render "Mes relances".
 *
 * @returns The page element.
 */
export function MyFollowupsPage() {
  const [cases, setCases] = useState<RegistrationCase[] | undefined>(undefined);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listMyRegistrationCases()
      .then((page) => {
        if (!cancelled) setCases(page.results);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function onUpdated(updated: RegistrationCase) {
    setCases((current) =>
      current?.map((registrationCase) =>
        registrationCase.id === updated.id ? updated : registrationCase,
      ),
    );
  }

  return (
    <div className="household-screen">
      <div className="bloc">
        <h1>{t('household.cases.title')}</h1>
        <p className="muted">{t('household.cases.lead')}</p>
      </div>
      <div className="bloc">
        {loadError ? (
          <p role="alert">{t('household.cases.loadError')}</p>
        ) : cases === undefined ? (
          <>
            <Skeleton height="8rem" />
            <Skeleton height="8rem" />
          </>
        ) : cases.length === 0 ? (
          <p className="muted">{t('household.cases.empty')}</p>
        ) : (
          cases.map((registrationCase) => (
            <CaseCard
              key={registrationCase.id}
              registrationCase={registrationCase}
              onUpdated={onUpdated}
            />
          ))
        )}
      </div>
    </div>
  );
}
